type Modal = {
  container: HTMLElement;
  initialFocus: HTMLElement;
  close: () => void;
  returnTargets: HTMLElement[];
  preventScroll: boolean;
};

const modals: Modal[] = [];
const inertElements = new Map<HTMLElement, boolean>();
let wasScrollLocked = false;
const focusableSelector = 'a[href], button:enabled, input:not([type="hidden"]):enabled, select:enabled, textarea:enabled, summary, [tabindex]:not([tabindex="-1"])';

function focusableElements(container: HTMLElement) {
  return Array.from(container.querySelectorAll<HTMLElement>(focusableSelector)).filter(
    (element) => element.tabIndex >= 0 && !element.closest('[inert], [hidden]') && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== "hidden"
  );
}

function updateInert() {
  document.body.classList.toggle("no-scroll", wasScrollLocked || modals.some((modal) => modal.preventScroll));
  inertElements.forEach((wasInert, element) => element.toggleAttribute("inert", wasInert));
  inertElements.clear();
  let current = modals[modals.length - 1]?.container;
  while (current && current !== document.body) {
    const parent = current.parentElement;
    if (!parent) break;
    Array.from(parent.children).forEach((sibling) => {
      if (sibling !== current && sibling instanceof HTMLElement && !sibling.matches("script, style, link") && sibling.dataset.modalOverlay !== modals[modals.length - 1]?.container.id) {
        inertElements.set(sibling, sibling.hasAttribute("inert"));
        sibling.setAttribute("inert", "");
      }
    });
    current = parent;
  }
}

function onKeyDown(event: KeyboardEvent) {
  const modal = modals[modals.length - 1];
  if (!modal) return;
  if (event.key === "Escape") {
    event.preventDefault();
    event.stopImmediatePropagation();
    modal.close();
  } else if (event.key === "Tab") {
    const elements = focusableElements(modal.container);
    const first = elements[0];
    const last = elements[elements.length - 1];
    if (!first) {
      event.preventDefault();
      modal.container.focus();
    } else if (event.shiftKey && (document.activeElement === first || !elements.includes(document.activeElement as HTMLElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

function onFocusIn(event: FocusEvent) {
  const modal = modals[modals.length - 1];
  if (modal && !modal.container.contains(event.target as Node)) {
    (focusableElements(modal.container)[0] || modal.container).focus();
  }
}

const observer = new MutationObserver(() => {
  updateInert();
  const modal = modals[modals.length - 1];
  if (modal && !modal.container.contains(document.activeElement)) modal.initialFocus.focus();
});

export function activateModal(container: HTMLElement, initialFocus: HTMLElement, close: () => void, opener: HTMLElement | null = document.activeElement as HTMLElement, preventScroll = true) {
  if (!modals.length) wasScrollLocked = document.body.classList.contains("no-scroll");
  const parent = [...modals].reverse().find((modal) => opener && modal.container.contains(opener));
  const returnTargets = [...(opener ? [opener] : []), ...(parent?.returnTargets || [])];
  const modal = { container, initialFocus, close, returnTargets, preventScroll };
  modals.push(modal);
  if (modals.length === 1) {
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("focusin", onFocusIn, true);
    observer.observe(document.body, { childList: true, subtree: true });
  }
  updateInert();
  initialFocus.focus();

  return () => {
    const wasTop = modals[modals.length - 1] === modal;
    modals.splice(modals.indexOf(modal), 1);
    updateInert();
    if (!modals.length) {
      observer.disconnect();
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("focusin", onFocusIn, true);
    }
    if (wasTop) {
      const next = modals[modals.length - 1];
      const target = returnTargets.find((element) => element.isConnected && !element.closest("[inert]") && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== "hidden");
      if (target) target.focus();
      else if (next) (focusableElements(next.container)[0] || next.container).focus();
    }
  };
}
